// Icon Map Utility - Maps icon names to React Icon components
// Supports pack + name storage format: { pack: "fi", name: "FiUser" }

import * as FiIcons from "react-icons/fi";
import * as BiIcons from "react-icons/bi";
import * as AiIcons from "react-icons/ai";
import * as MdIcons from "react-icons/md";
import * as HiIcons from "react-icons/hi";

// Icon packs configuration
export const iconPacks = {
    fi: { icons: FiIcons, label: "Feather Icons", prefix: "Fi" },
    bi: { icons: BiIcons, label: "Bootstrap Icons", prefix: "Bi" },
    ai: { icons: AiIcons, label: "Ant Design Icons", prefix: "Ai" },
    md: { icons: MdIcons, label: "Material Design", prefix: "Md" },
    hi: { icons: HiIcons, label: "Heroicons", prefix: "Hi" },
};

// Combined icon map for all packs
export const iconMap = {
    ...FiIcons,
    ...BiIcons,
    ...AiIcons,
    ...MdIcons,
    ...HiIcons,
};

/**
 * Get icon component by name string or pack+name object
 * @param {string|object} icon - Either "FiUser" or { pack: "fi", name: "FiUser" }
 * @returns {React.Component|null}
 */
export const getIconByName = (icon) => {
    if (!icon) return null;

    // Handle pack + name format
    if (typeof icon === "object" && icon.name) {
        const pack = iconPacks[icon.pack];
        if (pack && pack.icons[icon.name]) {
            return pack.icons[icon.name];
        }
    }

    // Handle simple string format
    if (typeof icon === "string") {
        return iconMap[icon] || null;
    }

    return null;
};

/**
 * Get searchable icon list for a specific pack
 * @param {string} packKey - Pack key like "fi", "bi", etc.
 * @returns {Array}
 */
export const getIconsByPack = (packKey) => {
    const pack = iconPacks[packKey];
    if (!pack) return [];

    return Object.keys(pack.icons)
        .filter((name) => name.startsWith(pack.prefix))
        .map((name) => ({
            pack: packKey,
            name,
            component: pack.icons[name],
        }));
};

/**
 * Get all icons from all packs
 * @returns {Array}
 */
export const getAllIcons = () => {
    const allIcons = [];
    Object.keys(iconPacks).forEach((packKey) => {
        allIcons.push(...getIconsByPack(packKey));
    });
    return allIcons;
};

/**
 * Search icons by name across all packs
 * @param {string} searchTerm
 * @param {string|null} packFilter - Optional pack filter
 * @returns {Array}
 */
export const searchIcons = (searchTerm = "", packFilter = null) => {
    const icons = packFilter ? getIconsByPack(packFilter) : getAllIcons();

    if (!searchTerm) return icons.slice(0, 400); // Limit for performance

    const lowerSearch = searchTerm.toLowerCase();
    return icons
        .filter((icon) => icon.name.toLowerCase().includes(lowerSearch))
        .slice(0, 400);
};
